import React, { useEffect, useState } from "react";
import {
    Card,
    Row,
    Col,
    Typography,
    Button,
    Table,
    Space,
    message,
    Modal,
    Input,
    Tooltip
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PrinterOutlined,
    SearchOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../../configs";
import { getUser } from "../../../utils/DataUser";
import dayjs from "dayjs";
import { canCreate, canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;
const defaultPageSize = 20;

export default function PemuatanList() {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: defaultPageSize,
        total: 0,
    });

    // Filter states
    const [printUrl, setPrintUrl] = useState(null);
    const [showPrintModal, setShowPrintModal] = useState(false);

    const [filters, setFilters] = useState({
        nomor_pemuatan: "",
        document_number: "",
        customer_name: ""
    });

    const fetchItems = async (page = 1, pageSize = defaultPageSize, newFilters = {}) => {
        setLoading(true);
        // Merge current filters with new ones
        const activeFilters = { ...filters, ...newFilters };
        if (Object.keys(newFilters).length > 0) {
            setFilters(activeFilters);
        }

        try {
            const res = await axiosInstance.get("/pemuatan", {
                params: {
                    page,
                    limit: pageSize,
                    nomor_pemuatan: activeFilters.nomor_pemuatan || undefined,
                    document_number: activeFilters.document_number || undefined,
                    customer_name: activeFilters.customer_name || undefined,
                },
            });
            const data = res.data?.data || [];

            const user = getUser();
            let finalData = data;
            if (user && user.id_level === 7) {
                finalData = data.filter(
                    (item) =>
                        item.customer_name === user.username ||
                        item.id_customer === user.username
                );
            }

            const total = res.data?.pagination?.total || 0;

            setItems(finalData);
            setPagination({
                current: page,
                pageSize,
                total,
            });
        } catch (err) {
            console.error(err);
            message.error("Gagal memuat daftar pemuatan");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleTableChange = (pager) => {
        fetchItems(pager.current, pager.pageSize);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Hapus Data Pemuatan?",
            content: "Tindakan ini akan mengembalikan stok barang yang sudah dimuat. Outgoing terkait akan kembali ke status DRAFT.",
            okText: "Ya, Hapus",
            okType: "danger",
            cancelText: "Batal",
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/pemuatan/${id}`);
                    message.success("Pemuatan berhasil dihapus, stok dikembalikan");
                    fetchItems(pagination.current);
                } catch (err) {
                    message.error(err.response?.data?.error || "Gagal menghapus pemuatan");
                }
            }
        });
    };

    const columns = [
        {
            title: "NO",
            dataIndex: "index",
            width: 70,
            align: "center",
            render: (text, record, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "NO PEMUATAN",
            dataIndex: "nomor_pemuatan",
            width: 150,
        },
        {
            title: "TANGGAL",
            dataIndex: "tanggal_pemuatan",
            width: 150,
            render: (val) => (val ? dayjs(val).format("DD-MM-YYYY") : "-"),
        },
        {
            title: "REF OUTGOING",
            dataIndex: "no_outgoing",
            width: 150,
        },
        {
            title: "NO POLISI",
            dataIndex: "no_polisi",
            width: 120,
        },
        {
            title: "DRIVER",
            dataIndex: "driver",
            width: 120,
        },
        {
            title: "AKSI",
            align: "center",
            width: 140,
            render: (text, record) => (
                <Space>
                    {canEdit(MODULES.INVENTORY) && (
                        <Tooltip title={getUser().id_level === 7 ? "Lihat Detil" : "Edit"}>
                            <Link to={`/inventory/updatemuat/${record.id_pemuatan}`}>
                                <Button
                                    icon={getUser().id_level === 7 ? <SearchOutlined /> : <EditOutlined />}
                                    size="small"
                                    type="primary"
                                />
                            </Link>
                        </Tooltip>
                    )}
                    <Button
                        icon={<PrinterOutlined />}
                        size="small"
                        onClick={() => {
                            setPrintUrl(`/#/print/pemuatan/${record.id_pemuatan}`);
                            setShowPrintModal(true);
                        }}
                    />
                    {canDelete(MODULES.INVENTORY) && getUser().id_level !== 7 && (
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => handleDelete(record.id_pemuatan)}
                        />
                    )}
                </Space>
            ),
        }
    ];

    return (
        <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col xs={24} md={12} style={{ marginBottom: { xs: 16, md: 0 } }}>
                    <Title level={3} style={{ marginBottom: 4 }}>
                        Pemuatan Barang
                    </Title>
                    <Text type="secondary">
                        Daftar proses pemuatan barang (pengurangan stok) berdasarkan dokumen pengeluaran.
                    </Text>
                </Col>
                <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchItems(1, defaultPageSize)}
                        >
                            Refresh
                        </Button>
                        {canCreate(MODULES.INVENTORY) && getUser().id_level !== 7 && (
                            <Link to="/inventory/addmuat">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    style={{
                                        background: "#1BC5BD",
                                        borderColor: "#1BC5BD",
                                        fontWeight: 600,
                                    }}
                                >
                                    Tambah Pemuatan
                                </Button>
                            </Link>
                        )}
                    </Space>
                </Col>
            </Row>

            {/* Filter Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={8}>
                    <Text strong>Cari No Pemuatan</Text>
                    <Input
                        allowClear
                        placeholder="Contoh: LOAD-001..."
                        style={{ marginTop: 4 }}
                        onChange={(e) => {
                            // Optional: auto search or just set state
                            // For consistency with other pages, we might want a state + Apply button or auto on Enter
                        }}
                        onPressEnter={(e) => fetchItems(1, defaultPageSize, { nomor_pemuatan: e.target.value })}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Cari No Dokumen</Text>
                    <Input
                        allowClear
                        placeholder="Cari No Dokumen..."
                        style={{ marginTop: 4 }}
                        onPressEnter={(e) => fetchItems(1, defaultPageSize, { document_number: e.target.value })}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Cari Pemilik Barang</Text>
                    <Input
                        allowClear
                        placeholder="Cari Pemilik..."
                        style={{ marginTop: 4 }}
                        onPressEnter={(e) => fetchItems(1, defaultPageSize, { customer_name: e.target.value })}
                    />
                </Col>
            </Row>

            <Table
                rowKey="id_pemuatan"
                dataSource={items}
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        `Total ${total} data | ${range[0]}-${range[1]}`,
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Cetak Surat Jalan"
                visible={showPrintModal}
                onCancel={() => {
                    setShowPrintModal(false);
                    setPrintUrl(null);
                }}
                footer={null}
                width={1000}
                style={{ top: 20 }}
                bodyStyle={{ height: '80vh', padding: 0 }}
            >
                {printUrl && (
                    <iframe
                        src={printUrl}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Print Preview"
                    />
                )}
            </Modal>
        </Card>
    );
}
